import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseText } from '../BaseText'
import { BaseButton } from '../buttons/BaseButton'

interface TimeSelectorProps {
  selectedTime: { hours: string; minutes: string }
  isFinalSelection?: boolean
  selectedDate?: Date | null
  startDate?: Date | null
  minDate?: Date
  maxDate?: Date
  onTimeSelect: (time: { hours: string; minutes: string }) => void
  onConfirm: () => void
  isRange?: boolean
}

export const DatePickerTimeSelector = ({
  selectedTime,
  isFinalSelection,
  selectedDate,
  startDate,
  minDate,
  maxDate,
  onTimeSelect,
  onConfirm,
  isRange,
}: TimeSelectorProps) => {
  const { t } = useTranslation()
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const minutes = ['00', '15', '30', '45']

  // For programmatic date changes we need to update the time selector
  useEffect(() => {
    if (!selectedDate) return

    // Get the time from the selected date
    const dateHours = selectedDate.getHours().toString().padStart(2, '0')
    const dateMinutes = (Math.floor(selectedDate.getMinutes() / 15) * 15).toString().padStart(2, '0')

    if (selectedTime.hours !== dateHours || selectedTime.minutes !== dateMinutes) {
      onTimeSelect({ hours: dateHours, minutes: dateMinutes })
    }
  }, [selectedDate, onTimeSelect, selectedTime, startDate])

  const isTimeDisabled = (hour: string, minute: string): boolean => {
    if (!selectedDate || (!minDate && !maxDate)) return false
    const timeToCheck = new Date(selectedDate)
    timeToCheck.setHours(Number.parseInt(hour))
    timeToCheck.setMinutes(Number.parseInt(minute))

    if (minDate && timeToCheck < minDate) return true
    if (maxDate && timeToCheck > maxDate) return true

    // For range selection, ensure end time is after start time
    if (isRange && isFinalSelection && startDate) {
      return timeToCheck <= startDate
    }

    return false
  }

  return (
    <>
      <Box p={3}>
        <Box className='time-select-container' width='100%'>
          <VStack align='stretch' spacing={4}>
            <Box>
              <Text mb={2} fontSize='10px' color='gray.600'>
                {t('date_picker.hour')}
              </Text>
              <Flex flexWrap='wrap' gap={1}>
                {hours.map(hour => {
                  const isDisabled = isTimeDisabled(hour, selectedTime.minutes)
                  return (
                    <Box
                      key={hour}
                      p={2}
                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      aria-selected={selectedTime.hours === hour}
                      color={selectedTime.hours === hour ? 'white' : isDisabled ? 'gray.300' : 'black'}
                      bg={selectedTime.hours === hour ? 'green.700' : 'transparent'}
                      _hover={{ bg: isDisabled ? 'transparent' : selectedTime.hours === hour ? 'green.900' : 'gray.100' }}
                      onClick={() => {
                        if (!isDisabled) onTimeSelect({ ...selectedTime, hours: hour })
                      }}
                      borderRadius='8px'
                      minWidth='30px'
                      textAlign='center'
                      fontSize='10px'
                      opacity={isDisabled ? 0.5 : 1}
                    >
                      {hour}
                    </Box>
                  )
                })}
              </Flex>
            </Box>
            <Box>
              <Text mb={2} fontSize='10px' color='gray.600'>
                {t('date_picker.minutes')}
              </Text>
              <Flex flexWrap='wrap' gap={1}>
                {minutes.map(minute => {
                  const isDisabled = isTimeDisabled(selectedTime.hours, minute)
                  return (
                    <Box
                      key={minute}
                      p={2}
                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      aria-selected={selectedTime.minutes === minute}
                      color={selectedTime.minutes === minute ? 'white' : isDisabled ? 'gray.300' : 'black'}
                      bg={selectedTime.minutes === minute ? 'green.700' : 'transparent'}
                      _hover={{ bg: isDisabled ? 'transparent' : selectedTime.minutes === minute ? 'green.900' : 'gray.100' }}
                      onClick={() => {
                        if (!isDisabled) onTimeSelect({ ...selectedTime, minutes: minute })
                      }}
                      borderRadius='8px'
                      minWidth='30px'
                      textAlign='center'
                      fontSize='10px'
                      opacity={isDisabled ? 0.5 : 1}
                    >
                      {minute}
                    </Box>
                  )
                })}
              </Flex>
            </Box>
          </VStack>
        </Box>
      </Box>
      <Divider />
      <Flex p={2} justifyContent='space-between' alignItems='center'>
        <BaseText variant='small' textStyle='xs/text-xs Regular' fontSize='8px' color='gray.600'>
          {!isRange && t('date_picker.select_date_and_time')}
          {isRange &&
            (isFinalSelection
              ? `${t('date_picker.select_end_date_and_time')}${selectedDate ? ` (${t('date_picker.after')} ${format(selectedDate, 'dd.MM.yyyy HH:mm')})` : ''}`
              : t('date_picker.select_date_and_time'))}
        </BaseText>
        <BaseButton
          variant='primary'
          size='small'
          text='Ok'
          isDisabled={!selectedDate || isTimeDisabled(selectedTime.hours, selectedTime.minutes)}
          onClick={onConfirm}
        />
      </Flex>
    </>
  )
}
